import useCourseEditing from '@/data/hook/useCourseEditing';
import {
  ChallengeInputs,
  createChallengeActivity,
  createChallengeQuestion,
  deleteChallenge,
  updateChallenge,
} from '@/data/services/activityElement/challengeServices';
import { updateTextQuestionIntoActivity } from '@/data/services/_deprecated/textActivityServices';
import { updateLesson } from '@/data/services/lessonServices';
import { isPulished } from '@/functions/handleCourseStatusIcon';
import { ChallengeActivity } from '@/models/_deprecated/ActivityElement';
import { ImageFolderEnum } from '@/models/CkEditor';
import Lesson from '@/models/Lesson';
import alert from '@/utils/shared/alert';
import {
  PencilIcon,
  PlusIcon,
  ArrowDownOnSquareIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import PedWorkspaceGuard from '@/components/shared/PedWorkspaceGuard';
import MainButton from '@/components/ui/custom/buttons/MainButton';
import { HtmlPreview } from '@/components/ui/custom/dataDisplay/HtmlPreview';
import Text from '@/components/ui/custom/dataDisplay/Text';
import ModalDisable from '@/components/ui/custom/modals/ModalDisable';
import MyCkeditor from '@/components/editor/MyCkeditor';
import { ScheduledLessonTypeEnum } from '@/models/ScheduledLesson';

import { VersioningStatusEnum } from '@/enums/VersioningStatus';
import { challengesQueryKeys } from '@/data/services/querykeys';
import { usePedWorkspaceStatus } from '@/data/context/PedWorkspaceContext';

type Props = {
  lesson: Lesson;
  blockId?: number;
  readOnly?: boolean;
  type?: ScheduledLessonTypeEnum;
  onUpdate?: (updatedLesson: Lesson) => void;
  slug?: string;
};

export function ChallengeEditing(props: Props) {
  const { isLimitedEdition } = usePedWorkspaceStatus();
  const { lesson, blockId, type, readOnly, onUpdate, slug = '' } = props;

  const { t: tChallenge } = useTranslation('translation', {
    keyPrefix: 'activity.manageActivity.challenge',
  });
  const { slugCourseName = slug } = useParams();
  const { allowBigChanges } = useCourseEditing();
  const canModifyChallenge = allowBigChanges && !isLimitedEdition;

  const [hasChanges, setHasChanges] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const isExtraLesson = type !== ScheduledLessonTypeEnum.NORMAL && !!type;
  const { queryFn, queryKey: challengeQueryKey } = challengesQueryKeys.get(
    lesson.id,
  );

  const { data: challenge, isInitialLoading: isLoading } = useQuery({
    queryFn,
    queryKey: challengeQueryKey,
  });

  const { handleSubmit, reset, control } = useForm<ChallengeInputs>({
    defaultValues: {
      content: challenge?.questions.length
        ? challenge.questions[0].content
        : '',
    },
  });
  const queryClient = useQueryClient();

  const handleUpdateLessonStatus = async (lesson: Lesson) => {
    if (!isPulished(lesson.status)) return lesson;
    const body = isExtraLesson
      ? { status: VersioningStatusEnum.VERSIONING }
      : {};
    return updateLesson({ lessonId: lesson.id, slugCourseName, blockId }, body);
  };

  const handleCreateChallenge = async (lessonId: number, content: string) => {
    const { id: challengeId } = await createChallengeActivity(lessonId);
    await createChallengeQuestion(challengeId, { content });
  };

  const handleUpdateChallenge = async (
    challenge: ChallengeActivity,
    lessonId: number,
    content: string,
  ) => {
    const targetChallenge =
      challenge.status === VersioningStatusEnum.PUBLISHED
        ? await updateChallenge({ lessonId, challengeId: challenge.id })
        : challenge;
    const targetQuestion =
      targetChallenge.questions[0] ??
      (await createChallengeQuestion(targetChallenge.id, { content }));
    await updateTextQuestionIntoActivity(
      targetChallenge.id,
      targetQuestion.id,
      {
        content,
      },
    );
  };

  const handleDeleteChallenge = async () => {
    setIsDeleting(true);
    let updatedLesson = lesson;
    try {
      updatedLesson = await handleUpdateLessonStatus(lesson);

      if (challenge?.id) {
        let challengeAux = challenge;
        if (challenge.status === VersioningStatusEnum.PUBLISHED) {
          challengeAux = await updateChallenge({
            lessonId: updatedLesson.id,
            challengeId: challenge.id,
          });
        }
        await deleteChallenge({
          lessonId: updatedLesson.id,
          challengeId: challengeAux.id,
        });
        setIsVisible(false);
        setIsEdit(false);
        alert.success(tChallenge('deleteSuccess'));
      }
      reset({
        content: '',
      });
      queryClient.removeQueries({ queryKey: challengesQueryKeys._def });
    } catch {
      alert.error(tChallenge('deleteFail'));
    } finally {
      if (updatedLesson !== lesson) onUpdate?.(updatedLesson);
      setIsDeleting(false);
    }
  };

  const onSubmit: SubmitHandler<ChallengeInputs> = async ({ content }) => {
    let updatedLesson = lesson;
    try {
      setIsRequesting(true);

      if (challenge?.id && isNaN(challenge.id)) {
        return;
      }

      updatedLesson = await handleUpdateLessonStatus(lesson);

      if (challenge?.id) {
        await handleUpdateChallenge(challenge, updatedLesson.id, content);
      } else {
        await handleCreateChallenge(updatedLesson.id, content);
      }

      setHasChanges(false);
      setIsEdit(false);
      reset({ content });
      alert.success(tChallenge('saveSuccess'));
    } catch {
      alert.error(tChallenge('saveError'));
    } finally {
      queryClient.invalidateQueries({ queryKey: challengesQueryKeys._def });
      if (updatedLesson !== lesson) onUpdate?.(updatedLesson);
      setIsRequesting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex gap-y-2.5 flex-col">
      <ConditionalRenderer condition={challenge?.id}>
        <Text
          text={tChallenge('content')}
          format="rubik-500"
          className="text-primary"
        />
      </ConditionalRenderer>
      {!isEdit ? (
        <HtmlPreview
          className={isLoading || isRequesting ? 'animate-pulse' : ''}
          format="book"
          testId="challengeEditor"
          html={
            challenge?.questions.length ? challenge.questions[0].content : ''
          }
        />
      ) : (
        <Controller
          control={control}
          name="content"
          render={({ field: { onChange } }) => (
            <MyCkeditor
              content={
                challenge?.questions.length
                  ? challenge.questions[0].content
                  : ''
              }
              format="book"
              testId="challengeEditor"
              disabled={!isEdit}
              folder={ImageFolderEnum.CHALLENGE}
              handleContentChange={value => {
                onChange(value);
                setHasChanges(true);
              }}
            />
          )}
        />
      )}
      <ModalDisable
        visible={isVisible}
        selectedObjectName={lesson.name}
        translationString="modalDisableChallenge"
        onClickCancel={() => {
          setIsVisible(false);
        }}
        onClickConfirm={handleDeleteChallenge}
        isRequesting={isDeleting}
      />
      <ConditionalRenderer condition={!readOnly}>
        <div className="flex justify-between items-center w-full">
          <PedWorkspaceGuard
            isBlocked={!challenge?.id && !canModifyChallenge}
            placement="right"
          >
            <MainButton
              icon={challenge?.id ? <PencilIcon /> : <PlusIcon />}
              dataTestId="editChallenge"
              text={tChallenge(challenge?.id ? 'edit' : 'add')}
              onClick={() => {
                setIsEdit(true);
              }}
              type="button"
              loading={isRequesting || isDeleting || isLoading}
              disabled={(!challenge?.id && !canModifyChallenge) || isEdit}
            />
          </PedWorkspaceGuard>
          <div
            className={`flex gap-3 ${
              challenge?.id || isEdit ? '' : 'invisible pointer-events-none'
            }`}
          >
            <ConditionalRenderer condition={challenge?.id}>
              <PedWorkspaceGuard
                isBlocked={!canModifyChallenge}
                placement="right"
              >
                <MainButton
                  icon={<TrashIcon />}
                  dataTestId="deleteChallenge"
                  text={tChallenge('delete')}
                  onClick={() => {
                    setIsVisible(true);
                  }}
                  type="button"
                  color="warning"
                  loading={isDeleting}
                  disabled={!canModifyChallenge || !isEdit || !challenge}
                />
              </PedWorkspaceGuard>
            </ConditionalRenderer>
            <MainButton
              text={tChallenge('cancel')}
              disabled={!isEdit}
              icon={<XMarkIcon />}
              type="reset"
              color="neutral"
              onClick={() => {
                reset({ content: challenge?.questions[0].content || '' });
                setHasChanges(false);
                setIsEdit(false);
              }}
            />
            <MainButton
              icon={<ArrowDownOnSquareIcon />}
              dataTestId="saveChallenge"
              text={tChallenge('save')}
              type="submit"
              loading={isRequesting || isDeleting || isLoading}
              disabled={!hasChanges || !isEdit}
            />
          </div>
        </div>
      </ConditionalRenderer>
    </form>
  );
}
